import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Grid,
    Autocomplete,
    Checkbox,
    Switch,
    FormControlLabel,
    Tooltip
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import PropTypes from 'prop-types';

// Timezone utility functions
const convertToUTC = (localHour) => {
    const now = new Date();
    const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), localHour, 0, 0);
    return localDate.getUTCHours();
};

const convertFromUTC = (utcHour) => {
    const now = new Date();
    const utcDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), utcHour, 0, 0));
    return utcDate.getHours();
};

const AdmicityCronScheduler = ({
    value,
    onChange,
    disabled = false,
    label = 'Synchronization Schedule'
}) => {
    const [syncEnabled, setSyncEnabled] = useState(true);
    const [selectedWeekDays, setSelectedWeekDays] = useState([1, 2, 3, 4, 5]);
    const [hourType, setHourType] = useState('at');
    const [selectedHours, setSelectedHours] = useState([8]); // Default to 8 AM local time
    const [hourInterval, setHourInterval] = useState(1);
    const [hourStart, setHourStart] = useState(8); // Default to 8 AM local time
    const [hourEnd, setHourEnd] = useState(18); // Default to 6 PM local time

    useEffect(() => {
        if (value === null || value === undefined) {
            setSyncEnabled(false);
            return;
        }

        if (typeof value === 'string' && value.includes(' ')) {
            parseCronExpression(value);
        } else if (typeof value === 'number' && value > 0) {
            setSyncEnabled(true);
            setHourType('every');
            setHourInterval(value);
        } else {
            setSyncEnabled(false);
        }
    }, [value]);

    const parseCronExpression = (cron) => {
        const parts = cron.split(' ');
        if (parts.length !== 6) return;

        const [, , hour, , , dayOfWeek] = parts;

        setSyncEnabled(true);

        // Parse hour (3rd field in .NET Quartz) - convert from UTC to local time
        if (hour.includes('-') && hour.includes('/')) {
            const [range, intervalPart] = hour.split('/');
            const [start, end] = range.split('-');
            setHourType('every');
            setHourInterval(parseInt(intervalPart));
            setHourStart(convertFromUTC(parseInt(start)));
            setHourEnd(convertFromUTC(parseInt(end)));
        } else if (hour.startsWith('*/')) {
            setHourType('every');
            setHourInterval(parseInt(hour.substring(2)));
        } else if (hour.includes(',')) {
            setHourType('at');
            setSelectedHours(hour.split(',').map(h => convertFromUTC(parseInt(h))));
        } else {
            setHourType('at');
            setSelectedHours([convertFromUTC(parseInt(hour))]);
        }

        // Parse day of week (6th field in .NET Quartz: 1=Sunday, 2=Monday, etc.)
        if (dayOfWeek.includes(',')) {
            setSelectedWeekDays(dayOfWeek.split(',').map(d => parseInt(d) - 1));
        } else if (dayOfWeek.includes('-')) {
            const [start, end] = dayOfWeek.split('-');
            const days = [];
            for (let i = parseInt(start); i <= parseInt(end); i++) {
                days.push(i - 1);
            }
            setSelectedWeekDays(days);
        } else if (dayOfWeek !== '?' && dayOfWeek !== '*') {
            setSelectedWeekDays([parseInt(dayOfWeek) - 1]);
        }
    };

    useEffect(() => {
        if (!syncEnabled) {
            onChange?.(null);
            return;
        }

        let newCron = '';

        // .NET Quartz format: second minute hour day month dayOfWeek
        // Second (always 0)
        newCron = '0';

        // Minute (always 0)
        newCron += ' 0';

        // Hour (3rd field) - convert from local time to UTC
        if (hourType === 'every') {
            if (hourStart !== hourEnd) {
                const utcStart = convertToUTC(hourStart);
                const utcEnd = convertToUTC(hourEnd);
                newCron += ` ${utcStart}-${utcEnd}/${hourInterval}`;
            } else {
                newCron += ` */${hourInterval}`;
            }
        } else {
            const utcHours = selectedHours.map(h => convertToUTC(h)).sort((a, b) => a - b);
            newCron += ` ${utcHours.join(',')}`;
        }

        // Day of month (4th field) and Day of week (6th field)
        // In .NET Quartz, you can't specify both day-of-month AND day-of-week
        if (selectedWeekDays.length > 0) {
            // Use specific weekdays, set day of month to ?
            newCron += ' ?';
            // Month (5th field - always *)
            newCron += ' *';
            // Day of week (6th field - 1=Sunday, 2=Monday, etc.)
            const quartzDays = selectedWeekDays.map(day => day + 1).sort((a, b) => a - b);
            newCron += ` ${quartzDays.join(',')}`;
        } else {
            // Use day of month, set day of week to ?
            newCron += ' *';
            // Month (5th field - always *)
            newCron += ' *';
            // Day of week (6th field - use ? when using day of month)
            newCron += ' ?';
        }

        onChange?.(newCron);
    }, [syncEnabled, selectedWeekDays, hourType, selectedHours, hourInterval, hourStart, hourEnd]);

    const weekDayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

    const getWeekDayOptions = () => {
        return weekDayNames.map((name, index) => ({
            value: index,
            label: name
        }));
    };

    const getHourOptions = () => {
        return Array.from({ length: 24 }, (_, i) => ({
            value: i,
            label: getHourLabel(i)
        }));
    };

    const getHourLabel = (hour) => {
        if (hour === 0) return '12:00 AM';
        if (hour < 12) return `${hour.toString().padStart(2, '0')}:00 AM`;
        if (hour === 12) return '12:00 PM';
        return `${(hour - 12).toString().padStart(2, '0')}:00 PM`;
    };

    const getSelectedWeekDaysForAutocomplete = () => {
        return selectedWeekDays.map(day => ({
            value: day,
            label: weekDayNames[day]
        }));
    };

    const getSelectedHoursForAutocomplete = () => {
        return selectedHours.map(hour => ({
            value: hour,
            label: getHourLabel(hour)
        }));
    };

    // Helper functions to get valid options for start/end time
    const getValidStartTimeOptions = () => {
        return Array.from({ length: 24 }, (_, i) => ({
            value: i,
            label: getHourLabel(i),
            disabled: hourType === 'every' && i >= hourEnd
        }));
    };

    const getValidEndTimeOptions = () => {
        return Array.from({ length: 24 }, (_, i) => ({
            value: i,
            label: getHourLabel(i),
            disabled: hourType === 'every' && i <= hourStart
        }));
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={syncEnabled}
                            onChange={(e) => setSyncEnabled(e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label={`Enable ${label}`}
                />
            </Box>

            {syncEnabled && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                                multiple
                                disableCloseOnSelect
                                limitTags={7}
                                options={getWeekDayOptions()}
                                value={getSelectedWeekDaysForAutocomplete()}
                                onChange={(event, newValue) => {
                                    // Ensure at least one day is always selected
                                    if (newValue.length === 0) {
                                        return; // Don't allow empty selection
                                    }
                                    setSelectedWeekDays(newValue.map(item => item.value));
                                }}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                disabled={disabled}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select days of the week"
                                    />
                                )}
                                renderOption={(props, option, { selected }) => {
                                    const isLastSelected = selectedWeekDays.length === 1 && selectedWeekDays.includes(option.value);
                                    return (
                                        <li {...props} key={option.value}>
                                            <Checkbox
                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                checked={selected}
                                                disabled={isLastSelected} // Disable if this is the last selected day
                                            />
                                            {option.label}
                                        </li>
                                    );
                                }}
                            />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth size="small" disabled={disabled}>
                                    <Select
                                        value={hourType}
                                        onChange={(e) => setHourType(e.target.value)}
                                    >
                                        <MenuItem value="at">at</MenuItem>
                                        <MenuItem value="every">every</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={hourType === 'every' ? 2 : 10}>
                                {hourType === 'every'
                                    ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Hour(s)"
                                            value={hourInterval}
                                            onChange={(e) => setHourInterval(parseInt(e.target.value) || 1)}
                                            disabled={disabled}
                                            inputProps={{ min: 1, max: 24 }}
                                        />
                                    )
                                    : (
                                        <Autocomplete
                                            multiple
                                            disableCloseOnSelect
                                            limitTags={2}
                                            options={getHourOptions()}
                                            value={getSelectedHoursForAutocomplete()}
                                            onChange={(event, newValue) => {
                                                setSelectedHours(newValue.map(item => item.value));
                                            }}
                                            getOptionLabel={(option) => option.label}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            disabled={disabled}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    label="Hour(s)"
                                                />
                                            )}
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props} key={option.value}>
                                                    <Checkbox
                                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                        checked={selected}
                                                    />
                                                    {option.label}
                                                </li>
                                            )}
                                        />
                                    )}
                            </Grid>
                            {hourType === 'every' && (
                                <>
                                    <Grid item xs={12} sm={1}>
                                        <Typography variant="body2" sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                                            between
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Tooltip title="Start time in local timezone" arrow>
                                            <FormControl fullWidth size="small" disabled={disabled}>
                                                <Select
                                                    value={hourStart}
                                                    onChange={(e) => setHourStart(parseInt(e.target.value))}
                                                >
                                                    {getValidStartTimeOptions().map((option) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            value={option.value}
                                                            disabled={option.disabled}
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={6} sm={1}>
                                        <Typography variant="body2" sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                                            and
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Tooltip title="End time in local timezone" arrow>
                                            <FormControl fullWidth size="small" disabled={disabled}>
                                                <Select
                                                    value={hourEnd}
                                                    onChange={(e) => setHourEnd(parseInt(e.target.value))}
                                                >
                                                    {getValidEndTimeOptions().map((option) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            value={option.value}
                                                            disabled={option.disabled}
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Tooltip>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

AdmicityCronScheduler.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    label: PropTypes.string
};

export default AdmicityCronScheduler;